import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Phone,
  CheckCircle2,
  Smartphone,
  Laptop,
  Tablet,
  Gamepad2,
  Droplets,
  Star,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { Button } from './ui/button';
import ContactSection from './ContactSection';
import TrustBadgesSection from './TrustBadgesSection';
import Footer from './Footer';
import QuoteModal from './QuoteModal';
import WhatsAppButton from './WhatsAppButton';
import ScrollToTopButton from './ScrollToTopButton';
import StickyMobileCTA from './StickyMobileCTA';
import FadeInOnScroll from './FadeInOnScroll';
import JsonLd from './JsonLd';
import { getServiceBySlug, services } from '../data/services';

const ICONS = { Smartphone, Laptop, Tablet, Gamepad2, Droplets };

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  useEffect(() => {
    if (!service) return;
    document.title = `${service.h1} | Nexera Phone & Tech Repair`;
    window.scrollTo(0, 0);
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <h1 className="text-4xl font-bold mb-4">Service not found</h1>
        <Button onClick={() => navigate('/')} style={{ backgroundColor: '#00B3B3', color: 'white' }}>
          Back to Home
        </Button>
      </div>
    );
  }

  const Icon = ICONS[service.icon] || Smartphone;
  const otherServices = services.filter((s) => s.slug !== service.slug).slice(0, 3);

  // JSON-LD: Service + FAQPage + Breadcrumb
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: {
      '@type': 'LocalBusiness',
      name: 'Nexera Phone & Tech Repair',
      telephone: '+12244199922',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2560 N Illinois 83',
        addressLocality: 'Round Lake Beach',
        addressRegion: 'IL',
        postalCode: '60073',
        addressCountry: 'US',
      },
    },
    areaServed: { '@type': 'City', name: 'Round Lake Beach, IL' },
    url: `https://nexerarepair.com/${service.slug}`,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: service.faq.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nexerarepair.com/' },
      { '@type': 'ListItem', position: 2, name: service.name, item: `https://nexerarepair.com/${service.slug}` },
    ],
  };

  return (
    <div className="min-h-screen has-sticky-mcta bg-white">
      <JsonLd id={`service-${service.slug}`} data={serviceSchema} />
      <JsonLd id={`faq-${service.slug}`} data={faqSchema} />
      <JsonLd id={`breadcrumb-${service.slug}`} data={breadcrumbSchema} />

      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="service-nav-home">
            <img
              src="https://customer-assets.emergentagent.com/job_phone-fix-quotes/artifacts/vsjgmfo7_NEXERA-02.png"
              alt="Nexera Phone & Tech Repair"
              className="h-8 sm:h-9 w-auto"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden grain-overlay">
        <div className="absolute inset-0 bg-gradient-to-br from-[#03121c] via-[#072a36] to-[#0f3a4a]" />
        <div
          className="blob"
          style={{
            width: 420,
            height: 420,
            top: '-100px',
            right: '-100px',
            background: 'radial-gradient(circle, #00B3B3 0%, transparent 65%)',
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 md:py-28">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm text-gray-300" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{service.name}</span>
          </nav>

          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
            style={{
              background: 'linear-gradient(135deg, #00B3B3 0%, #14e3e3 100%)',
              boxShadow: '0 10px 30px -10px rgba(0, 179, 179, 0.6)',
            }}
          >
            <Icon className="w-8 h-8 text-white" />
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            {service.h1}
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-6 max-w-3xl">{service.tagline}</p>
          <p className="text-gray-300 max-w-3xl mb-8">{service.description}</p>

          <div className="flex flex-wrap gap-3 mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm">
              <div className="flex">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" />
                ))}
              </div>
              5.0 Google Reviews
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Same-Day Service
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm">
              <span className="w-2 h-2 rounded-full bg-cyan-400" />
              90-Day Warranty
            </span>
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold"
              style={{ background: '#00B3B3', color: 'white' }}
            >
              {service.startingPrice}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => setIsQuoteModalOpen(true)}
              size="lg"
              className="text-white px-7 py-6 text-base rounded-lg transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: '#00B3B3',
                boxShadow: '0 4px 14px 0 rgba(0, 179, 179, 0.39)',
              }}
              data-testid={`service-${service.slug}-quote-btn`}
            >
              Get a Free Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              asChild
              className="px-7 py-6 text-base rounded-lg bg-white/5 backdrop-blur-sm"
              style={{ borderColor: '#00B3B3', borderWidth: '2px', color: '#00B3B3' }}
            >
              <a href="tel:2244199922">
                <Phone className="mr-2 h-5 w-5" />
                Call (224) 419-9922
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Issues we fix */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeInOnScroll className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Common {service.name} Issues We Fix
            </h2>
            <div className="w-20 h-1" style={{ backgroundColor: '#00B3B3' }} />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {service.issues.map((issue, i) => (
              <FadeInOnScroll key={issue} delay={i * 60}>
                <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 hover:border-[#00B3B3] transition-colors bg-white">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#00B3B3' }} />
                  <p className="text-gray-800">{issue}</p>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Service-specific FAQ */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #eef9f9 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <FadeInOnScroll className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {service.name} — Questions Answered
            </h2>
            <div className="w-20 h-1 mx-auto" style={{ backgroundColor: '#00B3B3' }} />
          </FadeInOnScroll>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {service.faq.map((faq, idx) => (
              <AccordionItem
                key={idx}
                value={`item-${idx}`}
                className="border border-gray-200 rounded-lg px-6 bg-white shadow-sm"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Other services */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <FadeInOnScroll className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Other Repair Services
            </h2>
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {otherServices.map((s) => {
              const SIcon = ICONS[s.icon] || Smartphone;
              return (
                <Link
                  key={s.slug}
                  to={`/${s.slug}`}
                  className="glass-card p-6 rounded-xl block group"
                  data-testid={`related-${s.slug}`}
                >
                  <div
                    className="inline-flex w-12 h-12 items-center justify-center rounded-xl mb-3"
                    style={{
                      background: 'linear-gradient(135deg, #00B3B3 0%, #14e3e3 100%)',
                    }}
                  >
                    <SIcon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{s.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{s.tagline}</p>
                  <span
                    className="inline-flex items-center text-sm font-semibold group-hover:gap-2 gap-1 transition-all"
                    style={{ color: '#00B3B3' }}
                  >
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <ContactSection />
      <TrustBadgesSection />
      <Footer />

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} />
      <ScrollToTopButton />
      <WhatsAppButton />
      <StickyMobileCTA onGetQuote={() => setIsQuoteModalOpen(true)} />
    </div>
  );
};

export default ServicePage;
